// Auto-layout engine — turns a room's EFFECTIVE HEPA / light-fixture counts
// (roomSystemsEngine.js) into generated canvas symbols (TakeoffConditionsDrawn
// rows tagged AutoSystem/AutoKey), and reconciles them against what's already
// there. Phases 4–5 of the 2026-09-11 engineering direction:
//
//   Engineering Formula -> Effective Quantity -> Generated Symbols
//
// never the reverse: a placed/deleted symbol NEVER redefines the engineering
// requirement. The estimator adopts a drawn count as an explicit override
// (Room Detail Flyout) if they want the two to agree.
//
// Identity mirrors the auto-wall convention (takeoffGeometry.js
// computeAutoWallSegments + db/37 sp_SyncAutoWallConditions): a stable key,
// `<system>:<roomId>:<index>`, is matched by sp_SyncAutoSystemConditions
// (db/69) so a layout refresh AMENDS rows in place (position only, and only
// on rows the estimator hasn't dragged — AutoPositionLocked) instead of
// deleting and recreating them.
//
// Reconciliation rule per (room, system), where N = effective count and
// G = the count generated last time (TakeoffRoomEngineering.*GeneratedCount):
//   G is null (never generated)  -> generate N
//   G != N (engineering changed) -> follow the engine: add keys up to N,
//                                   delete keys >= N
//   G == N                       -> the engine hasn't moved; any difference
//                                   between N and what's drawn is the
//                                   estimator's own edit (a deleted or hand-
//                                   placed symbol) and is left alone —
//                                   the flyout offers "adopt drawn count as
//                                   override" instead
// Positions: an r×c grid over the room's bounding box inset by a wall
// offset, filtered to the room's REAL polygon (a notched room only gets
// symbols inside its shape), optionally snapped to a ceiling-grid module,
// and lights are nudged off any HEPA within a minimum separation (first
// pass at coordinating the two ceiling systems). Pure functions, no DB.

import { roomVertices, pointInPolygon } from "./takeoffGeometry";

export const AUTO_SYSTEMS = ["hepa", "light"];
export const AUTO_SYSTEM_LAYER_KEYS = { hepa: "std_hepa_filters", light: "std_light_fixtures" };
export const GENERATED_COUNT_COLUMN = { hepa: "HEPAGeneratedCount", light: "LightingGeneratedCount" };
// Fallback placed width when the generating entity has no DefaultWidthFt.
export const AUTO_SYSTEM_DEFAULT_WIDTH_FT = { hepa: 4, light: 4 };

// Seeded as TakeoffVariables rows (db/69, Category='auto_layout'; Setup ▸
// Estimate Engine ▸ Engine Variables). Never hardcode a new one here alone.
export const DEFAULT_LAYOUT_TUNABLES = {
  AUTO_LAYOUT_WALL_OFFSET_FT: 1,      // inset of the placement grid from the room boundary
  AUTO_LAYOUT_MIN_SEPARATION_FT: 2,   // a light this close to a HEPA gets nudged
  AUTO_LAYOUT_CEILING_GRID_FT: 0,     // >0 = snap positions to cell centers of this module (e.g. 2 for a 2x4 grid); 0 = off
};

export const autoKey = (system, roomId, index) => `${system}:${roomId}:${index}`;
export function parseAutoKey(key) {
  const [system, roomId, index] = String(key || "").split(":");
  return { system, roomId: +roomId, index: +index };
}

const round2 = (v) => Math.round(v * 100) / 100;
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

function bboxOf(verts) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const v of verts) { minX = Math.min(minX, v.x); minY = Math.min(minY, v.y); maxX = Math.max(maxX, v.x); maxY = Math.max(maxY, v.y); }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Nearest cell CENTER of a grid module anchored at `origin`.
function snapToCellCenter(v, origin, g) {
  return origin + (Math.floor((v - origin) / g) + 0.5) * g;
}

// Evenly-spread subset of `count` points from an ordered candidate list.
function spread(pts, count) {
  if (pts.length <= count) return pts;
  const out = [];
  for (let i = 0; i < count; i++) out.push(pts[Math.floor((i * pts.length) / count)]);
  return out;
}

// Grid positions for `count` symbols inside a room's real shape. Returns
// { points, cell } where cell is the final grid pitch (used for nudging).
export function layoutGridPositions(room, count, tunables = {}) {
  const t = { ...DEFAULT_LAYOUT_TUNABLES, ...tunables };
  const verts = roomVertices(room);
  if (!verts || !(count > 0)) return { points: [], cell: { w: 0, h: 0 } };
  const bb = bboxOf(verts);
  if (!(bb.w > 0) || !(bb.h > 0)) return { points: [], cell: { w: 0, h: 0 } };
  const off = Math.max(0, Math.min(t.AUTO_LAYOUT_WALL_OFFSET_FT, bb.w / 4, bb.h / 4));
  const x0 = bb.x + off, y0 = bb.y + off;
  const w = Math.max(bb.w - 2 * off, 0.1), h = Math.max(bb.h - 2 * off, 0.1);
  const grid = t.AUTO_LAYOUT_CEILING_GRID_FT > 0 ? t.AUTO_LAYOUT_CEILING_GRID_FT : 0;

  let cols = Math.max(1, Math.round(Math.sqrt((count * w) / h)));
  let rows = Math.max(1, Math.ceil(count / cols));
  let best = { points: [], cell: { w: w / cols, h: h / rows } };
  for (let attempt = 0; attempt < 24; attempt++) {
    const pts = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        let x = x0 + (w * (c + 0.5)) / cols;
        let y = y0 + (h * (r + 0.5)) / rows;
        if (grid) {
          const sx = snapToCellCenter(x, bb.x, grid), sy = snapToCellCenter(y, bb.y, grid);
          if (pointInPolygon({ x: sx, y: sy }, verts)) { x = sx; y = sy; }
        }
        const p = { x: round2(x), y: round2(y) };
        if (!pointInPolygon(p, verts)) continue;
        if (pts.some(q => dist(q, p) < 0.05)) continue; // two cells snapped to one module
        pts.push(p);
      }
    }
    const cell = { w: w / cols, h: h / rows };
    if (pts.length >= count) return { points: spread(pts, count), cell };
    if (pts.length > best.points.length) best = { points: pts, cell };
    // Densify along whichever axis currently has the coarser pitch.
    if (w / cols >= h / rows) cols++; else rows++;
  }
  return best; // fewer than requested — an extremely thin/odd shape; caller places what it can
}

// Nudge any point within `minSep` of an obstacle to a nearby free position
// (half a cell in ±x/±y, inside the polygon, not itself conflicting). A point
// with no free neighbor stays put — a layout with a mild overlap beats a
// missing fixture.
export function nudgeAwayFromObstacles(points, obstacles, cell, room, tunables = {}) {
  const t = { ...DEFAULT_LAYOUT_TUNABLES, ...tunables };
  const minSep = t.AUTO_LAYOUT_MIN_SEPARATION_FT;
  if (!(minSep > 0) || !obstacles.length) return points;
  const verts = roomVertices(room);
  if (!verts) return points;
  const conflicts = (p, others) => obstacles.some(o => dist(o, p) < minSep) || others.some(q => q !== p && dist(q, p) < 0.05);
  const out = points.map(p => ({ ...p }));
  for (let i = 0; i < out.length; i++) {
    if (!conflicts(out[i], out)) continue;
    const dx = cell.w / 2 || minSep, dy = cell.h / 2 || minSep;
    const candidates = [
      { x: out[i].x + dx, y: out[i].y }, { x: out[i].x - dx, y: out[i].y },
      { x: out[i].x, y: out[i].y + dy }, { x: out[i].x, y: out[i].y - dy },
      { x: out[i].x + dx, y: out[i].y + dy }, { x: out[i].x - dx, y: out[i].y - dy },
    ].map(c => ({ x: round2(c.x), y: round2(c.y) }));
    const fit = candidates.find(c => pointInPolygon(c, verts) && !conflicts(c, out));
    if (fit) out[i] = fit;
  }
  return out;
}

// Which catalog Item/Assembly represents `system` for this room: the room's
// template's own Standard Equipment row tagged with that system first
// (RoomTemplateEquipment, db/55 — "ISO 7 gets THIS HEPA"), else the first
// active drawable entity tagged with it (a global default). null = nothing
// tagged yet, so nothing can be generated (the admin tags an Item under
// Setup ▸ Estimate Engine ▸ Items ▸ "Generated system").
export function resolveAutoSystemEntity(system, room, templateEquipment = [], drawableEntities = [], roomElements = []) {
  const candidates = drawableEntities.filter(e =>
    e.AutoSystem === system && e.Style === "count" && (e.Kind === "item" || e.Kind === "assembly"));
  if (!candidates.length) return null;
  // Room Build-Up (db/70): the room's OWN element for this system is the
  // entity to generate — the estimator chose it. Template and global
  // fallbacks below only apply to rooms without an explicit element (and
  // with gating on, such a room has an effective count of 0 anyway).
  for (const e of roomElements) {
    if (e.RoomID !== room.RoomID) continue;
    if (e.AutoSystem !== system && e.ElementQuantityBasis !== `engine_${system}`) continue;
    const match = candidates.find(c =>
      (e.Kind === "item" && c.Kind === "item" && c.ItemID === e.ItemID) ||
      (e.Kind === "assembly" && c.Kind === "assembly" && c.AssemblyID === e.AssemblyID));
    if (match) return match;
  }
  if (room.RoomTemplateID) {
    for (const e of templateEquipment) {
      if (e.RoomTemplateID !== room.RoomTemplateID || !e.InScope) continue;
      const match = candidates.find(c =>
        (e.Kind === "item" && c.Kind === "item" && c.ItemID === e.ItemID) ||
        (e.Kind === "assembly" && c.Kind === "assembly" && c.AssemblyID === e.AssemblyID));
      if (match) return match;
    }
  }
  return [...candidates].sort((a, b) => String(a.Code || "").localeCompare(String(b.Code || "")))[0];
}

function geometryOf(row) {
  try { return JSON.parse(row.GeometryJson || "{}"); } catch { return {}; }
}

// Reconcile one room. Inputs:
//   effective    - { hepa: N, light: N } (roomSystemsEngine effectiveQuantity)
//   entities     - { hepa: entity|null, light: entity|null } (resolveAutoSystemEntity)
//   existingRows - this revision's TakeoffConditionsDrawn rows (any room)
//   engRow       - the room's TakeoffRoomEngineering row (or undefined)
// Returns { upserts, deleteKeys, generated: {hepa,light}, drawn: {hepa,light} }
// for sp_SyncAutoSystemConditions; `generated` is what the engineering row
// should record as *GeneratedCount after this plan is applied.
export function computeAutoSystemPlan({ room, effective, entities, existingRows = [], engRow, tunables = {} }) {
  const upserts = [], deleteKeys = [];
  const generated = {}, drawn = {};
  const roomRows = existingRows.filter(r => r.RoomID === room.RoomID);
  const obstacles = []; // final positions of earlier systems (HEPAs first) — lights avoid them

  for (const system of AUTO_SYSTEMS) {
    const N = Math.max(0, Math.round(+effective?.[system] || 0));
    const entity = entities?.[system] || null;
    const autoRows = roomRows.filter(r => r.AutoSystem === system && r.AutoKey);
    drawn[system] = roomRows.filter(r => r.Style === "count" && r.EntityAutoSystem === system).length;
    const stored = engRow?.[GENERATED_COUNT_COLUMN[system]];
    const prevGen = stored != null ? +stored : (autoRows.length ? autoRows.length : null);

    if (!entity) { generated[system] = prevGen; continue; }

    // Which key indices should exist after this pass.
    const followEngine = prevGen === null || prevGen !== N;
    const indices = followEngine
      ? Array.from({ length: N }, (_, i) => i)
      : autoRows.map(r => parseAutoKey(r.AutoKey).index).filter(Number.isFinite).sort((a, b) => a - b);
    if (followEngine) for (const r of autoRows) if (parseAutoKey(r.AutoKey).index >= N) deleteKeys.push(r.AutoKey);

    const byIndex = new Map(autoRows.map(r => [parseAutoKey(r.AutoKey).index, r]));
    const { points, cell } = layoutGridPositions(room, indices.length, tunables);
    const placed = nudgeAwayFromObstacles(points, obstacles, cell, room, tunables);
    const widthFt = +entity.DefaultWidthFt > 0 ? +entity.DefaultWidthFt : AUTO_SYSTEM_DEFAULT_WIDTH_FT[system];

    indices.forEach((idx, ordinal) => {
      const existing = byIndex.get(idx);
      const computed = placed[ordinal];
      if (existing) {
        const geom = geometryOf(existing);
        if (existing.AutoPositionLocked) { obstacles.push({ x: +geom.x, y: +geom.y }); return; } // estimator moved it — keep
        if (!computed) { obstacles.push({ x: +geom.x, y: +geom.y }); return; }
        obstacles.push(computed);
        if (Math.hypot((+geom.x || 0) - computed.x, (+geom.y || 0) - computed.y) < 0.05) return; // unchanged
        upserts.push({
          key: existing.AutoKey, system, room_id: room.RoomID,
          geometry: { ...geom, x: computed.x, y: computed.y, widthFt: geom.widthFt || widthFt, angleDeg: 0, wallNormalDeg: 0, flip: false },
        });
        return;
      }
      if (!computed) return; // couldn't fit — generate what fits
      obstacles.push(computed);
      upserts.push({
        key: autoKey(system, room.RoomID, idx), system, room_id: room.RoomID,
        kind: entity.Kind, item_id: entity.Kind === "item" ? entity.ItemID : null, assembly_id: entity.Kind === "assembly" ? entity.AssemblyID : null,
        geometry: { x: computed.x, y: computed.y, angleDeg: 0, wallNormalDeg: 0, widthFt, flip: false },
      });
    });

    generated[system] = followEngine ? N : prevGen;
  }
  return { upserts, deleteKeys, generated, drawn };
}
